import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const REGEX_ERRO_CHUNK = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

const obterChaveTentativa = (identificador: string) => `lazy-retry:${identificador}`;

const lerTentativa = (identificador: string) => {
  if (typeof window === 'undefined') return false;

  try {
    return window.sessionStorage.getItem(obterChaveTentativa(identificador)) === '1';
  } catch {
    return false;
  }
};

const marcarTentativa = (identificador: string) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(obterChaveTentativa(identificador), '1');
  } catch {
    // ignora falhas de storage
  }
};

const limparTentativa = (identificador: string) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(obterChaveTentativa(identificador));
  } catch {
    // ignora falhas de storage
  }
};

const ehErroDeChunk = (erro: unknown) => {
  const mensagem = erro instanceof Error ? erro.message : String(erro);
  return REGEX_ERRO_CHUNK.test(mensagem);
};

/**
 * Evita tela branca quando o navegador fica com um entrypoint antigo em cache
 * e tenta buscar um chunk lazy com hash que já não existe mais após deploy.
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  identificador: string,
): LazyExoticComponent<T> =>
  lazy(async () => {
    try {
      const modulo = await importer();
      limparTentativa(identificador);
      return modulo;
    } catch (erro) {
      if (ehErroDeChunk(erro) && !lerTentativa(identificador) && typeof window !== 'undefined') {
        marcarTentativa(identificador);
        window.location.reload();

        return new Promise<never>(() => {
          // mantém o fallback enquanto o reload acontece
        });
      }

      limparTentativa(identificador);
      throw erro;
    }
  });
